import React, { useEffect, useMemo, useState } from "react";
import TableRow from "@mui/material/TableRow";
import Switch from "@mui/material/Switch";
import { PageTitle } from "components/common/Text";
import { PrimaryButton } from "components/common/Buttons";
import { ActionBar } from "components/common/Wrapper";
import { TableBodyCell } from "components/table/table.style";
import CustomTable from "components/table/table.component";
import { Popup } from "components/common/popup/popup.component";
import { Spinner } from "components/common/spinner/spinner.component";
import { showSuccess } from "utils/notifications";
import { getSortList } from "utils/util-functions";
import { MANAGE_MODEM_FIRMWARE_COLUMNS, MANAGE_VERSIONS_COLUMNS } from "./manage-versions.config";
import RegisterVersionDialog from "./register-version-dialog.component";
import {
  deleteSoftwareVersion,
  getManagedSoftwareVersions,
  getManagedModemFirmware,
  ManagedModemFirmware,
  ManagedSoftwareVersion,
  setSoftwareVersionFlags,
} from "./manage-versions.api";

export default function ManageVersionsPage(): JSX.Element {
  const [versions, setVersions] = useState<ManagedSoftwareVersion[]>([]);
  const [modemFirmware, setModemFirmware] = useState<ManagedModemFirmware[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ManagedSoftwareVersion | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [showRegister, setShowRegister] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("");

  const fetchVersions = async () => {
    setIsLoading(true);
    try {
      const [softwareData, modemData] = await Promise.all([
        getManagedSoftwareVersions(),
        getManagedModemFirmware().catch(() => []),
      ]);
      setVersions(softwareData);
      setModemFirmware(modemData);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
  }, []);

  const handleRequestSort = (order: string, orderBy: string) => {
    setSortOrder(order);
    setSortBy(orderBy);
  };

  const sortedVersions: ManagedSoftwareVersion[] = useMemo(() => {
    const sortColumn = MANAGE_VERSIONS_COLUMNS.find(({ id }) => id === sortBy);
    return getSortList(versions, sortBy, sortOrder, sortColumn?.type);
  }, [versions, sortBy, sortOrder]);

  const handleToggle = async (row: ManagedSoftwareVersion, field: "allowDownload" | "allowVpn", value: boolean) => {
    setSavingId(row.softwareVersionID);
    try {
      await setSoftwareVersionFlags({
        softwareVersionId: row.softwareVersionID,
        allowDownload: field === "allowDownload" ? value : row.allowDownload,
        allowVpn: field === "allowVpn" ? value : row.allowVpn,
      });
      setVersions((prev) =>
        prev.map((r) => (r.softwareVersionID === row.softwareVersionID ? { ...r, [field]: value } : r)),
      );
      showSuccess("Software version updated");
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteSoftwareVersion(deleteTarget.softwareVersionID);
      setVersions((prev) => prev.filter((r) => r.softwareVersionID !== deleteTarget.softwareVersionID));
      showSuccess("Software version deleted");
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <PageTitle>Manage Software Versions</PageTitle>

      <ActionBar style={{ justifyContent: "flex-end" }}>
        <PrimaryButton onClick={() => setShowRegister(true)}>Register Firmware</PrimaryButton>
      </ActionBar>

      <CustomTable boxShadow>
        <CustomTable.Header columns={MANAGE_VERSIONS_COLUMNS} onRequestSort={handleRequestSort} />

        <CustomTable.Body isLoading={isLoading} deviceData={sortedVersions}>
          {sortedVersions.map((row: ManagedSoftwareVersion) => (
            <TableRow key={row.softwareVersionID}>
              <TableBodyCell>{row.swVersion}</TableBodyCell>
              <TableBodyCell>{row.portalVersion}</TableBodyCell>
              <TableBodyCell>{row.approvedSwVersion}</TableBodyCell>

              <TableBodyCell>
                <Switch
                  checked={row.allowDownload}
                  disabled={savingId === row.softwareVersionID}
                  onChange={(e) => handleToggle(row, "allowDownload", e.target.checked)}
                />
              </TableBodyCell>

              <TableBodyCell>
                <Switch
                  checked={row.allowVpn}
                  disabled={savingId === row.softwareVersionID}
                  onChange={(e) => handleToggle(row, "allowVpn", e.target.checked)}
                />
              </TableBodyCell>

              <TableBodyCell style={{ width: 120 }}>
                <PrimaryButton color="error" onClick={() => setDeleteTarget(row)}>
                  Delete
                </PrimaryButton>
              </TableBodyCell>
            </TableRow>
          ))}
        </CustomTable.Body>
      </CustomTable>

      <PageTitle style={{ marginTop: "32px" }}>Modem Firmware</PageTitle>
      <CustomTable boxShadow>
        <CustomTable.Header columns={MANAGE_MODEM_FIRMWARE_COLUMNS} />
        <CustomTable.Body isLoading={isLoading} deviceData={modemFirmware}>
          {modemFirmware.map((row: ManagedModemFirmware) => (
            <TableRow key={row.eiSoftwareImageId}>
              <TableBodyCell style={{ overflowWrap: "anywhere" }}>{row.fileName}</TableBodyCell>
              <TableBodyCell>{row.downloadPath}</TableBodyCell>
              <TableBodyCell>{row.model}</TableBodyCell>
              <TableBodyCell>{row.targetBand}</TableBodyCell>
              <TableBodyCell>{row.current ? "Yes" : "No"}</TableBodyCell>
            </TableRow>
          ))}
        </CustomTable.Body>
      </CustomTable>

      <Popup
        title="Delete software version"
        warning
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        primaryBtn={{ text: "Delete", onClick: handleDelete, disabled: isDeleting }}
        secondaryBtn={{ text: "Cancel", onClick: () => setDeleteTarget(null) }}
      >
        <div>
          Delete <b>{deleteTarget?.portalVersion}</b> ({deleteTarget?.swVersion})? It will be removed from the portal
          and from device update lists. A version currently running on a device cannot be deleted.
        </div>
      </Popup>

      {showRegister && (
        <RegisterVersionDialog open={showRegister} onClose={() => setShowRegister(false)} onAdd={fetchVersions} />
      )}

      <Popup title="Loading data..." open={isLoading && !versions.length}>
        <Spinner />
      </Popup>
    </>
  );
}
